import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { AgentesOcm, MetricasDia, Usuario } from '../lib/tipos'

const horas = (segundos: number): string => {
  const total = Math.round(segundos / 60)
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, '0')}m`
}

/** Quién puede tocar el cruce de agentes de OCM. */
const CONFIGURAN_OCM = ['supervisor', 'jefe_ventas', 'admin']

export function Metricas({ usuario }: { usuario: Usuario }) {
  const clienteQuery = useQueryClient()
  const [dia, setDia] = useState(() => new Date().toISOString().slice(0, 10))

  const metricas = useQuery({
    queryKey: ['metricas', dia],
    queryFn: () => api<MetricasDia>(`/api/metricas?dia=${dia}`),
  })

  const configuraOcm = CONFIGURAN_OCM.includes(usuario.rol)

  const agentes = useQuery({
    queryKey: ['agentes-ocm'],
    queryFn: () => api<AgentesOcm>('/api/metricas/agentes'),
    enabled: configuraOcm,
  })

  const asignar = useMutation({
    mutationFn: ({ usuarioId, agente }: { usuarioId: number; agente: string }) =>
      api('/api/metricas/agentes', { metodo: 'POST', cuerpo: { usuario_id: usuarioId, agente } }),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['agentes-ocm'] })
      void clienteQuery.invalidateQueries({ queryKey: ['metricas'] })
    },
  })

  const datos = metricas.data
  const soloLoSuyo = usuario.rol === 'comercial'

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-bold">Métricas</h1>
        {datos?.alcance && (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {datos.alcance}
          </span>
        )}
        <input
          type="date"
          value={dia}
          onChange={(e) => setDia(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950"
        />
      </div>
      <p className="mb-5 text-xs text-slate-500">
        Conversión = clientes con pedido ÷ llamadas atendidas. CUPS por hora = CUPS ÷ tiempo al
        teléfono. Ojo: son las definiciones viejas. Ricardo va a mandar el documento de métricas,
        y ahí la conversión pasa a ser llamadas con venta ÷ total de llamadas, sin contar las
        manuales.
      </p>

      {datos && !datos.hay_llamadas && (
        <p className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Ese día no hay llamadas importadas de OCM, así que la conversión y los CUPS por hora
          salen vacíos. Lo que ves son solo las ventas del CRM.
        </p>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { etiqueta: 'Entrantes', valor: datos?.llamadas.entrantes },
          { etiqueta: 'Salientes', valor: datos?.llamadas.salientes },
          { etiqueta: 'Atendidas', valor: datos?.llamadas.atendidas },
          { etiqueta: 'Perdidas', valor: datos?.llamadas.perdidas },
          { etiqueta: 'Clientes', valor: datos?.clientes },
          { etiqueta: 'CUPS', valor: datos?.cups },
          { etiqueta: 'Conversión', valor: datos?.conversion == null ? '—' : `${datos.conversion} %` },
          { etiqueta: 'CUPS por hora', valor: datos?.cups_hora ?? '—' },
        ].map((tarjeta) => (
          <div
            key={tarjeta.etiqueta}
            className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {tarjeta.etiqueta}
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{tarjeta.valor ?? '—'}</p>
          </div>
        ))}
      </div>

      {!soloLoSuyo && (
        <div className="mb-6 overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400 dark:bg-slate-800">
              <tr>
                <th className="px-4 py-2">Comercial</th>
                <th className="px-4 py-2 text-right">Atendidas</th>
                <th className="px-4 py-2 text-right">Entr./Sal.</th>
                <th className="px-4 py-2 text-right">Al teléfono</th>
                <th className="px-4 py-2 text-right">Clientes</th>
                <th className="px-4 py-2 text-right">CUPS</th>
                <th className="px-4 py-2 text-right">Conversión</th>
                <th className="px-4 py-2 text-right">CUPS/h</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {datos?.comerciales.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-slate-400">
                    Ese día no hay actividad.
                  </td>
                </tr>
              )}
              {datos?.comerciales.map((fila) => (
                <tr key={fila.id}>
                  <td className="px-4 py-2">{fila.nombre}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fila.atendidas}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                    {fila.entrantes}/{fila.salientes}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-500">{horas(fila.segundos)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fila.clientes}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{fila.cups}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {fila.conversion === null ? '—' : `${fila.conversion} %`}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fila.cups_hora ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configuraOcm && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-1 text-sm font-bold">Agentes de OCM</h2>
          <p className="mb-3 text-xs text-slate-500">
            Para cruzar las llamadas con las ventas hay que decir cómo se llama cada comercial en
            OCM. Al asignarlo, las llamadas ya importadas se recruzan solas. Al crear un usuario
            desde <code>bin/usuario.php</code> se puede dejar ya vinculado.
          </p>

          {agentes.data?.sin_cruzar.length ? (
            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Sin asignar:{' '}
              {agentes.data.sin_cruzar
                .map((agente) => `${agente.agente_ocm} (${agente.llamadas})`)
                .join(' · ')}
            </p>
          ) : null}

          <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {agentes.data?.usuarios.map((persona) => (
              <li key={persona.id} className="flex flex-wrap items-center gap-3 py-2">
                <span>{persona.nombre}</span>
                <span className="text-xs text-slate-400">{persona.rol}</span>
                <input
                  defaultValue={persona.ocm_usuario ?? ''}
                  placeholder="nombre o extensión en OCM"
                  onBlur={(e) => {
                    if (e.target.value !== (persona.ocm_usuario ?? '')) {
                      asignar.mutate({ usuarioId: persona.id, agente: e.target.value })
                    }
                  }}
                  className="ml-auto w-56 rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950"
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
